import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, MapPin, Briefcase, Globe, Upload, Download, Info, X } from 'lucide-react';
import { searchAPI, buyersAPI, buyerGroupsAPI, researchAPI } from '../services/api';
import CsvUploadResults from '../components/CsvUploadResults';
import ResultSizeSelect from '../components/ResultSizeSelect';
import RecentSearches from '../components/RecentSearches';
import { useCredits } from '../contexts/CreditsContext';
import { useOnboarding } from '../contexts/OnboardingContext';
import SetupErrorMessage from '../components/SetupErrorMessage';
import { looksLikeUSLocation } from '../utils/usLocation';
import { noBusinessesMessage } from '../utils/searchMessages';
import '../components/ResearchPanel.css';
import './SearchPage.css';

const SearchPage = () => {
  const { credits, refreshCredits } = useCredits();
  const { refresh: refreshOnboarding, step } = useOnboarding();
  const searchStep = step('first_search');
  const [resultSize, setResultSize] = useState(20);
  const [maxResultSize, setMaxResultSize] = useState(100);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    industry: '',
    location: ''
  });
  const [googleDomain, setGoogleDomain] = useState(''); // no default country: the user chooses
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [recentKey, setRecentKey] = useState(0);

  // Optional AI research of new results (off by default)
  const [researchProfiles, setResearchProfiles] = useState([]);
  const [autoResearch, setAutoResearch] = useState(false);
  const [researchProfileId, setResearchProfileId] = useState('');

  // CSV Upload state
  const [isUploading, setIsUploading] = useState(false);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [showColumnInfo, setShowColumnInfo] = useState(false);
  const [columnInfo, setColumnInfo] = useState(null);
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [csvResults, setCsvResults] = useState(null);
  const [showCsvResults, setShowCsvResults] = useState(false);

  // Group selection flow
  const [showGroupChoiceModal, setShowGroupChoiceModal] = useState(false);
  const [groupOptions, setGroupOptions] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [groupError, setGroupError] = useState('');
  const [groupChoice, setGroupChoice] = useState({
    mode: 'skip',
    groupId: '',
    newGroupName: '',
    description: '',
    color: '#3B82F6'
  });


  useEffect(() => {
    searchAPI.getStatus()
      .then((res) => {
        const cap = res.data?.status?.max_result_size;
        if (cap) setMaxResultSize(cap);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    researchAPI.listProfiles()
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res.data?.profiles) ? res.data.profiles : [];
        setResearchProfiles(list);
        const def = list.find((p) => p.is_default) || list[0];
        setResearchProfileId(def ? String(def.id) : '');
      })
      .catch(() => { if (!cancelled) setResearchProfiles([]); });
    return () => { cancelled = true; };
  }, []);

  const balanceKnown = credits !== null && credits !== undefined && typeof credits.total === 'number';
  const balanceTotal = balanceKnown ? credits.total : null;
  const cannotAfford = balanceKnown && balanceTotal < 20;

  // Keep the selected size affordable when the balance drops
  useEffect(() => {
    if (balanceTotal === null) return;
    setResultSize((cur) => {
      if (cur <= balanceTotal) return cur;
      return Math.max(20, Math.floor(balanceTotal / 20) * 20);
    });
  }, [balanceTotal, maxResultSize]);

  useEffect(() => {
    if (showGroupChoiceModal && groupOptions.length === 0 && !loadingGroups) {
      fetchGroupOptions();
    }
  }, [showGroupChoiceModal]);

  const fetchGroupOptions = async () => {
    try {
      setLoadingGroups(true);
      const response = await buyerGroupsAPI.getAll();
      if (response.data && response.data.success === false) {
        setGroupError('No groups yet. Create one or skip grouping.');
        setGroupOptions([]);
        return;
      }
      const groups = response.data?.groups || response.data || [];
      if (!Array.isArray(groups) || groups.length === 0) {
        setGroupError('No groups yet. Create one or skip grouping.');
        setGroupOptions([]);
        return;
      }
      setGroupOptions(groups);
      setGroupError('');
    } catch (err) {
      console.error('Error fetching groups:', err);
      setGroupError('No groups yet. Create one or skip grouping.');
      setGroupOptions([]);
    } finally {
      setLoadingGroups(false);
    }
  };

  const startSearchFlow = () => {
    if (!googleDomain) {
      setErrorMessage('Choose which Google domain to search first, so results come from the right country.');
      return;
    }
    setGroupError('');
    setShowGroupChoiceModal(true);
  };

  const applyGroupChoice = async (choiceSnapshot, newBuyerIds) => {
    if (!newBuyerIds || newBuyerIds.length === 0) return;
    const ids = newBuyerIds.map(id => parseInt(id, 10)).filter(Boolean);
    if (ids.length === 0) return;
    if (choiceSnapshot.mode === 'skip') return;

    try {
      if (choiceSnapshot.mode === 'existing') {
        const groupId = parseInt(choiceSnapshot.groupId, 10);
        await buyerGroupsAPI.addMembers(groupId, ids);
        const groupName = groupOptions.find(g => g.id === groupId)?.name || 'selected group';
        setSuccessMessage(prev => prev ? `${prev}. Tagged ${ids.length} buyer(s) to ${groupName}` : `Tagged ${ids.length} buyer(s) to ${groupName}`);
      } else if (choiceSnapshot.mode === 'new') {
        const response = await buyerGroupsAPI.bulkCreateAndAssign(
          choiceSnapshot.newGroupName.trim(),
          ids,
          (choiceSnapshot.description || '').trim(),
          choiceSnapshot.color || '#3B82F6'
        );
        const message = response.data?.message || `Created group "${choiceSnapshot.newGroupName}" and tagged ${ids.length} buyer(s)`;
        setSuccessMessage(prev => prev ? `${prev}. ${message}` : message);
      }
    } catch (err) {
      console.error('Group assignment error:', err);
      setErrorMessage(err.response?.data?.error || 'Search succeeded but group assignment failed.');
    }
  };

  const executeSearch = async (choiceSnapshot) => {
    setIsSearching(true);
    setSuccessMessage(null);
    setErrorMessage(null);

    try {
      // Create background search job
      const response = await searchAPI.run({
        industry: filters.industry,
        location: filters.location,
        result_size: resultSize,
        google_domain: googleDomain,
        ...(autoResearch && researchProfileId ? { research_profile_id: Number(researchProfileId) } : {})
      });

      // Get job ID from response
      const jobId = response.data.job_id;
      setSuccessMessage(`Search started! You can navigate away — we'll notify you when it's done.`);

      // Store job in localStorage so Layout can keep polling if user navigates away
      localStorage.setItem('pending_search_job', JSON.stringify({
        jobId,
        industry: filters.industry,
        location: filters.location,
        choiceSnapshot,
        startedAt: Date.now()
      }));

      // Poll for job completion
      const pollInterval = setInterval(async () => {
        try {
          const jobResponse = await searchAPI.getJobStatus(jobId);
          const job = jobResponse.data.job;

          if (job.status === 'completed') {
            clearInterval(pollInterval);
            localStorage.removeItem('pending_search_job');

            // Extract results from job
            const result = job.result || {};
            const buyersFound = result.buyers_found || 0;
            const buyersAdded = result.buyers_added || 0;
            const duplicates = result.duplicates_skipped || 0;
            const dncSkipped = result.dnc_skipped || 0;
            const newBuyerIds = result.added_buyer_ids || [];

            // Build success message
            let message = `Search complete! Found ${buyersFound} businesses, added ${buyersAdded} new prospects`;
            if (duplicates > 0) message += ` (${duplicates} duplicates skipped)`;
            if (dncSkipped > 0) message += ` (${dncSkipped} DNC skipped)`;
            const noWebsite = Number(result.pipeline_stats?.no_website) || 0;
            if (noWebsite > 0) message += `. ${noWebsite} with no website`;
            const creditsSpent = result.credits_spent || 0;
            const unsaved = result.unsaved_for_credits || 0;
            message += ` — ${creditsSpent} credit${creditsSpent === 1 ? '' : 's'} used`;
            if (unsaved > 0) message += `. ${unsaved} prospect${unsaved === 1 ? '' : 's'} not saved because you ran out of credits`;
            refreshCredits();
            refreshOnboarding();
            setRecentKey((k) => k + 1);

            if (buyersFound === 0) {
              // Do not claim success when Google returned nothing
              setSuccessMessage(null);
              setErrorMessage(noBusinessesMessage(filters.industry, filters.location));
            } else {
              setSuccessMessage(message);
            }
            setSearchResults([]);
            setIsSearching(false);

            // Apply group choice automatically to new buyers
            if (choiceSnapshot && newBuyerIds.length > 0) {
              await applyGroupChoice(choiceSnapshot, newBuyerIds);
            }
          } else if (job.status === 'failed') {
            clearInterval(pollInterval);
            localStorage.removeItem('pending_search_job');
            setErrorMessage(job.error_message || 'Search failed');
            setRecentKey((k) => k + 1);
            setSearchResults([]);
            setIsSearching(false);
          } else if (job.status === 'processing') {
            setSuccessMessage(`Search running in background — you can navigate away and we'll notify you when done.`);
          }
        } catch (pollError) {
          console.error('Error polling job status:', pollError);
        }
      }, 2000); // Poll every 2 seconds

      // Cleanup polling after 10 minutes (timeout)
      setTimeout(() => {
        clearInterval(pollInterval);
        if (isSearching) {
          setErrorMessage('Search timed out. Please check the Search Jobs page for results.');
          setIsSearching(false);
        }
      }, 600000);

    } catch (error) {
      console.error('Search error:', error);
      if (error.response?.status === 402) {
        const d = error.response.data || {};
        const needed = d.needed ?? resultSize;
        const available = d.available ?? (credits ? credits.total : '?');
        setErrorMessage(`Not enough credits: this search needs up to ${needed} and you have ${available}. Pick a smaller size or buy more credits.`);
        setSearchResults([]);
        setIsSearching(false);
        return;
      }
      setErrorMessage(error.response?.data?.error || 'An error occurred starting the search. Please try again.');
      setSearchResults([]);
      setIsSearching(false);
    }
  };

  const handleConfirmGroupChoice = () => {
    setGroupError('');
    if (groupChoice.mode === 'existing' && !groupChoice.groupId) {
      setGroupError('Please select a group');
      return;
    }
    if (groupChoice.mode === 'new' && !groupChoice.newGroupName.trim()) {
      setGroupError('Please enter a group name');
      return;
    }

    const choiceSnapshot = { ...groupChoice };
    setShowGroupChoiceModal(false);
    executeSearch(choiceSnapshot);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploading(true);
    setSuccessMessage(null);
    setErrorMessage(null);

    try {
      const response = await buyersAPI.uploadCSV(file, updateExisting);
      const summary = response.data.summary;

      // Set CSV results for color-coded display
      setCsvResults({
        added: summary.added || 0,
        updated: summary.updated || 0,
        duplicates: summary.duplicates_skipped || 0,
        invalid: summary.invalid_rows || 0,
        failed: summary.failed || 0
      });

      // Close the upload modal and show results modal
      setShowCsvModal(false);
      setShowCsvResults(true);
      refreshOnboarding(); // an imported prospect counts as the first search
    } catch (error) {
      console.error('CSV upload error:', error);
      setErrorMessage(error.response?.data?.error || 'Failed to upload CSV. Please try again.');
    } finally {
      setIsUploading(false);
      // Reset file input
      e.target.value = '';
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await buyersAPI.downloadCSVTemplate();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'buyer_upload_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Template download error:', error);
      setErrorMessage('Failed to download template');
    }
  };

  const truncate = (text, max = 18) => {
    if (!text) return '';
    return text.length > max ? `${text.slice(0, max)}…` : text;
  };

  const handleToggleColumnInfo = async () => {
    if (!showColumnInfo && !columnInfo) {
      try {
        const response = await buyersAPI.getCSVColumnsInfo();
        setColumnInfo(response.data.column_info);
      } catch (error) {
        console.error('Column info error:', error);
      }
    }
    setShowColumnInfo(!showColumnInfo);
  };

  return (
    <div className="search-page">
      <div className="search-header">
        <h1>Prospect Search</h1>
        <p>Search and discover new potential prospects</p>
      </div>

      {searchStep?.status === 'blocked' && (
        <div className="alert alert-warning search-setup-hint">
          {searchStep.description} <Link to={searchStep.route}>Go to Billing</Link>
        </div>
      )}

      <div className="search-container">
        <div className="filters-row">
          <div className="filter-item">
            <label htmlFor="search-industry">
              <Briefcase size={14} />
              Industry
            </label>
            <input
              id="search-industry"
              type="text"
              placeholder="e.g., Plumbing, Roofing, HVAC"
              value={filters.industry}
              onChange={(e) => setFilters({ ...filters, industry: e.target.value })}
            />
          </div>
          <div className="filter-item">
            <label htmlFor="search-location">
              <MapPin size={14} />
              Location
            </label>
            <input
              id="search-location"
              type="text"
              placeholder="e.g., London, Manchester"
              value={filters.location}
              onChange={(e) => setFilters({ ...filters, location: e.target.value })}
              onKeyPress={(e) => e.key === 'Enter' && !cannotAfford && startSearchFlow()}
            />
          </div>
          <div className="filter-item">
            <label htmlFor="search-google-domain">
              <Globe size={14} />
              Google Domain
            </label>
            <select
              id="search-google-domain"
              value={googleDomain}
              onChange={(e) => setGoogleDomain(e.target.value)}
              required
            >
              <option value="" disabled>Choose a country...</option>
              <option value="google.co.uk">google.co.uk (UK)</option>
              <option value="google.com">google.com (US)</option>
              <option value="google.com.au">google.com.au (AU)</option>
              <option value="google.ca">google.ca (CA)</option>
              <option value="google.co.nz">google.co.nz (NZ)</option>
              <option value="google.ie">google.ie (IE)</option>
              <option value="google.de">google.de (DE)</option>
              <option value="google.fr">google.fr (FR)</option>
              <option value="google.co.in">google.co.in (IN)</option>
              <option value="google.co.za">google.co.za (ZA)</option>
            </select>
            {googleDomain !== 'google.com' && googleDomain !== 'google.com.au' && googleDomain !== 'google.ca' && looksLikeUSLocation(filters.location) && (
              <p className="search-domain-hint">
                This looks like a US location. Use google.com (US) for better results.{' '}
                <button type="button" className="btn btn-sm btn-secondary" onClick={() => setGoogleDomain('google.com')}>
                  Switch to google.com
                </button>
              </p>
            )}
          </div>
        </div>

        <ResultSizeSelect
          value={resultSize}
          onChange={setResultSize}
          balance={credits ? credits.total : undefined}
          max={maxResultSize}
        />

        <div className="research-auto-option">
          <label>
            <input
              type="checkbox"
              checked={autoResearch && researchProfiles.length > 0}
              disabled={researchProfiles.length === 0}
              onChange={(e) => setAutoResearch(e.target.checked)}
            />
            {' '}AI Research new results automatically
          </label>
          {autoResearch && researchProfiles.length > 0 && (
            <select
              value={researchProfileId}
              onChange={(e) => setResearchProfileId(e.target.value)}
              aria-label="Research profile"
            >
              {researchProfiles.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          )}
          {researchProfiles.length === 0 && (
            <small>Create a research profile in <Link to="/settings#ai">Settings</Link> first.</small>
          )}
          {autoResearch && researchProfiles.length > 0 && (
            <small>Uses your own AI key for each prospect found.</small>
          )}
        </div>

        <div className="search-actions-row">
          {cannotAfford && (
            <p className="search-credit-warning">
              You need at least 20 credits to run a search. <Link to="/buyers">Buy more credits on the Prospects page.</Link>
            </p>
          )}
          <div className="search-actions">
            <button
              className="btn csv-tools-btn"
              type="button"
              onClick={() => setShowCsvModal(true)}
              disabled={isUploading}
            >
              <Upload size={16} />
              Upload CSV
            </button>
            <button className="btn btn-primary search-btn" onClick={startSearchFlow} disabled={isSearching || cannotAfford}>
              <Search size={18} />
              {isSearching ? 'Searching...' : 'Search'}
            </button>
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="alert alert-success">
          {successMessage}
        </div>
      )}
      {errorMessage && (
        <div className="alert alert-error">
          <SetupErrorMessage message={errorMessage} />
          {errorMessage.startsWith('Not enough credits') && (
            <> <Link to="/buyers">Buy more credits on the Prospects page.</Link></>
          )}
        </div>
      )}

      <div className="search-results">
        {searchResults.length === 0 && !isSearching && (
          <div className="empty-state">
            <span className="empty-state-icon">
              <Search size={26} />
            </span>
            <h3>Start Your Search</h3>
            <p>Enter search terms and filters to discover new prospects</p>
          </div>
        )}
        {isSearching && (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Searching...</p>
          </div>
        )}
      </div>

      <RecentSearches refreshKey={recentKey} />

      {/* CSV Modal */}
      {showCsvModal && (
        <div className="search-modal-overlay" onClick={() => setShowCsvModal(false)}>
          <div className="search-modal" onClick={(e) => e.stopPropagation()}>
            <div className="search-modal-header">
              <h3>Upload Buyers from CSV</h3>
              <button className="icon-button" onClick={() => setShowCsvModal(false)}>
                <X size={16} />
              </button>
            </div>
            <div className="search-modal-body">
              <div className="csv-upload-controls">
                <label className="btn btn-upload">
                  <Upload size={18} />
                  {isUploading ? 'Uploading...' : 'Upload CSV'}
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    disabled={isUploading}
                    className="file-input-hidden"
                  />
                </label>

                <button className="btn btn-secondary" onClick={handleDownloadTemplate}>
                  <Download size={18} />
                  Download Template
                </button>

                <button className="btn btn-secondary" onClick={handleToggleColumnInfo}>
                  <Info size={18} />
                  {showColumnInfo ? 'Hide' : 'Show'} Column Info
                </button>
              </div>

              <div className="csv-upload-options">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={updateExisting}
                    onChange={(e) => setUpdateExisting(e.target.checked)}
                  />
                  Update existing buyers (if unchecked, duplicates will be skipped)
                </label>
              </div>

              {showColumnInfo && columnInfo && (
                <div className="column-info-panel">
                  <h4>CSV Format</h4>
                  <p className="column-info-intro">
                    Your CSV file should have these column headers in the first row:
                  </p>

                  <div className="csv-preview">
                    <div className="csv-header-row">
                      {columnInfo.required_columns?.map((col) => (
                        <div key={col.name} className="csv-header-cell required-cell" title={col.name}>
                          {truncate(col.name, 16)}
                        </div>
                      ))}
                      {columnInfo.optional_columns?.map((col) => (
                        <div key={col.name} className="csv-header-cell optional-cell" title={col.name}>
                          {truncate(col.name, 16)}
                        </div>
                      ))}
                    </div>
                    <div className="csv-example-row">
                      {columnInfo.required_columns?.map((col) => (
                        <div key={col.name} className="csv-data-cell" title={col.example}>
                          {truncate(col.example, 20)}
                        </div>
                      ))}
                      {columnInfo.optional_columns?.map((col) => (
                        <div key={col.name} className="csv-data-cell" title={col.example}>
                          {truncate(col.example, 20)}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="column-info-legend">
                    <p><strong className="legend-required">Required:</strong> business_name, industry, location, and (email OR phone)</p>
                    <p><strong className="legend-optional">Optional:</strong> website, address, contact_method, status</p>
                  </div>
                </div>
              )}
            </div>
            <div className="search-modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowCsvModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Group choice before searching */}
      {showGroupChoiceModal && (
        <div className="search-modal-overlay" onClick={() => setShowGroupChoiceModal(false)}>
          <div className="search-modal" onClick={(e) => e.stopPropagation()}>
            <div className="search-modal-header">
              <h3>Tag search results to a group</h3>
              <button className="icon-button" onClick={() => setShowGroupChoiceModal(false)}>
                <X size={16} />
              </button>
            </div>
            <div className="search-modal-body">
              <div className="group-mode-grid">
                <button
                  className={`mode-chip ${groupChoice.mode === 'existing' ? 'active' : ''}`}
                  onClick={() => setGroupChoice({ ...groupChoice, mode: 'existing' })}
                  type="button"
                >
                  Add to existing
                </button>
                <button
                  className={`mode-chip ${groupChoice.mode === 'new' ? 'active' : ''}`}
                  onClick={() => setGroupChoice({ ...groupChoice, mode: 'new' })}
                  type="button"
                >
                  Create new group
                </button>
                <button
                  className={`mode-chip ${groupChoice.mode === 'skip' ? 'active' : ''}`}
                  onClick={() => setGroupChoice({ ...groupChoice, mode: 'skip' })}
                  type="button"
                >
                  Skip grouping
                </button>
              </div>

              {groupChoice.mode === 'existing' && (
                <div className="form-group">
                  <label>Select a group</label>
                  {loadingGroups && <div className="help-text">Loading groups...</div>}
                  {!loadingGroups && (
                    <select
                      value={groupChoice.groupId}
                      onChange={(e) => setGroupChoice({ ...groupChoice, groupId: e.target.value })}
                    >
                      <option value="">Choose a group</option>
                      {groupOptions.map(group => (
                        <option key={group.id} value={group.id}>
                          {group.name} ({group.buyer_count || 0} buyers)
                        </option>
                      ))}
                    </select>
                  )}
                  {!loadingGroups && groupOptions.length === 0 && (
                    <div className="help-text">No groups yet. Create one below.</div>
                  )}
                </div>
              )}

              {groupChoice.mode === 'new' && (
                <>
                  <div className="form-group">
                    <label>Group name</label>
                    <input
                      type="text"
                      value={groupChoice.newGroupName}
                      onChange={(e) => setGroupChoice({ ...groupChoice, newGroupName: e.target.value })}
                      placeholder="e.g., Plumbers London"
                    />
                  </div>
                  <div className="form-group">
                    <label>Description (optional)</label>
                    <textarea
                      value={groupChoice.description}
                      onChange={(e) => setGroupChoice({ ...groupChoice, description: e.target.value })}
                      placeholder="Short note to remind you what this group is for"
                      rows={3}
                    />
                  </div>
                  <div className="form-group">
                    <label>Color</label>
                    <input
                      type="color"
                      value={groupChoice.color}
                      onChange={(e) => setGroupChoice({ ...groupChoice, color: e.target.value })}
                      className="color-input"
                    />
                  </div>
                </>
              )}

              {groupError && <div className="alert alert-error group-error">{groupError}</div>}
            </div>
            <div className="search-modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowGroupChoiceModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleConfirmGroupChoice} disabled={isSearching}>
                {isSearching ? 'Searching...' : 'Continue Search'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCsvResults && csvResults && (
        <CsvUploadResults
          results={csvResults}
          onClose={() => {
            setShowCsvResults(false);
            setCsvResults(null);
          }}
        />
      )}
    </div>
  );
};

export default SearchPage;
